import { useCallback, useMemo, useRef, useState } from 'react';
import { MapShape } from '@larki/mp/doc';
import localforage from 'localforage';

import { useCombinedQuoteMode } from './useCombinedQuoteMode';
import { useQuote2dMapWorker } from './useQuote2dMapWorker';
import { getUser } from '../utilities/storage';
import { useAppDispatch } from './useAppDispatch';
import { Frontiers } from '@larki/mp/crdt-client';
import { jobStatusEnum } from '@larki/shared/type/job';
import { useLazyQuote2dMapQuery } from '../redux/rtk';
import { setQuotes } from '../redux/reducers/order.v2';
import { Quote2dMapWorkerOutput } from '@/utilities/quote2dMap';
import {
  _2dMapCombinedQuote,
  CustomOrderQuoteItem,
  PriceOverrideRecord,
} from '@larki/shared/type/order.v2';

export function applyOverridesToCustomOrders(
  items: CustomOrderQuoteItem[],
  overrides: PriceOverrideRecord[]
): CustomOrderQuoteItem[] {
  if (overrides.length === 0) return items;
  const overrideMap = new Map(
    overrides
      .filter((ov) => ov.product.startsWith('custom_'))
      .map((ov) => [ov.product, ov.overrideSubtotal])
  );
  if (overrideMap.size === 0) return items;
  return items.map((o) => {
    const subtotal = overrideMap.get(`custom_${o.id}`);
    if (subtotal !== undefined) return { ...o, price: subtotal ?? 'TBC' };
    return o;
  });
}

export function toCustomOrderQuoteItems(
  orders: Array<{
    id: string;
    name: string;
    description?: string | null;
    color: [number, number, number];
    price?: number | 'TBC';
  }>
): CustomOrderQuoteItem[] {
  return orders.map((o) => ({
    id: o.id,
    name: o.name,
    description: o.description ?? null,
    color: o.color,
    price: o.price ?? 'TBC',
  }));
}

export type Quote2dMapInputs = Pick<
  Quote2dMapWorkerOutput,
  'locality' | 'layers'
>;

type CheckoutState = {
  frontiers: Frontiers;
  versionId: number;
  versionStatus?: (typeof jobStatusEnum)[number] | null;
  // map2d.state lags mp-server's async persistence, so the payment page can't
  // trust it to hold the ops `frontiers` reference — carry the source state.
  snapshot?: Uint8Array;
};

type LastQuoteInputs = {
  jobId: number;
  inputs: Quote2dMapInputs;
  membershipPriceId?: string | null;
  customOrders?: CustomOrderQuoteItem[];
  map2dVersionId?: number | null;
};

export const useQuote2dMap = (_setLoading?: (loading: boolean) => void) => {
  const dispatch = useAppDispatch();
  const [quote2dMapFromInputs] = useLazyQuote2dMapQuery();
  const quote2dMapWorker = useQuote2dMapWorker();

  // Cache the last successful quote inputs so previewResetQuote can re-run
  // the pricing engine without asking callers to re-supply them. Bumped by
  // quoteVersion so downstream effects re-fire when the primary quote changes.
  const lastQuoteInputsRef = useRef<LastQuoteInputs | null>(null);
  const [quoteVersion, setQuoteVersion] = useState(0);
  const onCombinedQuote = useCallback(
    ({ jobId, quote }: { jobId: number; quote: _2dMapCombinedQuote }) => {
      dispatch(
        setQuotes({
          jobId,
          freeUserQuote: quote,
          memberQuote: quote,
        })
      );
    },
    [dispatch]
  );
  const {
    ensureMode,
    fetchAndApplyCombined,
    reset: resetVersionMode,
    getCachedMode,
  } = useCombinedQuoteMode({
    onCombinedQuote,
  });

  const getCheckoutState = useCallback((jobId: number) => {
    return localforage.getItem<CheckoutState>(`checkout-${jobId}`);
  }, []);

  const setCheckoutState = useCallback(
    (
      jobId: number,
      frontiers: Frontiers,
      versionId: number,
      versionStatus?: (typeof jobStatusEnum)[number] | null,
      snapshot?: Uint8Array
    ) => {
      const checkoutState: CheckoutState = {
        frontiers,
        versionId,
        versionStatus,
        snapshot,
      };
      localforage.setItem(`checkout-${jobId}`, checkoutState);
    },
    []
  );

  const doQuote = useCallback(
    async (
      jobId: number,
      inputs: Quote2dMapInputs,
      options?: {
        membershipPriceId?: string | null;
        customOrders?: CustomOrderQuoteItem[];
        forceRefetch?: boolean;
        map2dVersionId?: number | null;
        honorPriceLock?: boolean;
      }
    ) => {
      const {
        membershipPriceId,
        customOrders,
        forceRefetch,
        map2dVersionId,
        honorPriceLock,
      } = options ?? {};
      const preferCacheValue = !forceRefetch;
      const [freeUserQuote, memberQuote] = await Promise.all([
        quote2dMapFromInputs(
          {
            ...inputs,
            membershipPriceId,
            asMember: false,
            jobId,
            map2dVersionId: map2dVersionId ?? undefined,
            customOrders,
            honorPriceLock,
          },
          preferCacheValue
        ).unwrap(),
        quote2dMapFromInputs(
          {
            ...inputs,
            membershipPriceId,
            asMember: true,
            jobId,
            map2dVersionId: map2dVersionId ?? undefined,
            customOrders,
            honorPriceLock,
          },
          preferCacheValue
        ).unwrap(),
      ]);

      const quoteInputs = {
        ...inputs,
        membershipPriceId,
        customOrders,
        map2dVersionId: map2dVersionId ?? undefined,
      };
      dispatch(setQuotes({ jobId, freeUserQuote, memberQuote, quoteInputs }));
      lastQuoteInputsRef.current = {
        jobId,
        inputs,
        membershipPriceId,
        customOrders,
        map2dVersionId,
      };
      setQuoteVersion((v) => v + 1);
      return { freeUserQuote, memberQuote, quoteInputs };
    },
    [dispatch, quote2dMapFromInputs]
  );

  /**
   * Re-run the pricing engine with specific overrides suppressed, so the UI
   * can show what a reset of those products would produce. Does not dispatch
   * setQuotes — the caller extracts the subtotals it needs.
   */
  const previewResetQuote = useCallback(
    async (
      products: string[],
      asMember: boolean
    ): Promise<_2dMapCombinedQuote | null> => {
      const last = lastQuoteInputsRef.current;
      if (!last || products.length === 0) return null;
      return quote2dMapFromInputs(
        {
          ...last.inputs,
          membershipPriceId: last.membershipPriceId,
          asMember,
          jobId: last.jobId,
          map2dVersionId: last.map2dVersionId ?? undefined,
          customOrders: last.customOrders,
          excludeOverrideProducts: products,
        },
        true
      ).unwrap();
    },
    [quote2dMapFromInputs]
  );
  const quoteShapes = useCallback(
    async (
      jobId: number,
      shapes: MapShape[],
      membershipPriceId?: string | null,
      placeGeometry?: GeoJSON.Point | null,
      options?: {
        versionId?: number | null;
        customOrders?: CustomOrderQuoteItem[];
        forceRefetch?: boolean;
        honorPriceLock?: boolean;
      }
    ) => {
      const inputs = await quote2dMapWorker([
        {
          shapes,
          sessionToken: getUser().token,
          placeGeometry,
        },
      ]);

      const versionId = options?.versionId ?? null;
      const customOrders = options?.customOrders;
      const forceRefetch = options?.forceRefetch;
      const honorPriceLock = options?.honorPriceLock;

      // Behaviour:
      // Per versionId we detect once whether a combined quote exists:
      //   First time we see a versionId, hit fetchAndApplyCombined.
      //     If it returns data, cache mode='combined' and always use the
      //       combined endpoint for that version from then on.
      //     If it returns nothing, cache mode='live' and always fall back
      //       to doQuote for that version (no further combined calls).
      // If versionId is falsy, we always do a live quote.
      if (versionId) {
        const cachedMode = getCachedMode(versionId);
        if (cachedMode === 'combined') {
          await fetchAndApplyCombined(versionId, jobId);
        } else if (cachedMode === 'live') {
          await doQuote(jobId, inputs, {
            membershipPriceId,
            customOrders,
            forceRefetch,
            map2dVersionId: versionId,
            honorPriceLock,
          });
        } else {
          const mode = await ensureMode(versionId, jobId);
          if (mode === 'live') {
            await doQuote(jobId, inputs, {
              membershipPriceId,
              customOrders,
              forceRefetch,
              map2dVersionId: versionId,
              honorPriceLock,
            });
          }
        }
      } else {
        await doQuote(jobId, inputs, {
          membershipPriceId,
          customOrders,
          forceRefetch,
          honorPriceLock,
        });
      }
      return {
        inputs: { locality: inputs.locality, layers: inputs.layers },
        coverage: inputs.coverage,
      };
    },
    [
      doQuote,
      ensureMode,
      fetchAndApplyCombined,
      getCachedMode,
      quote2dMapWorker,
    ]
  );

  return useMemo(
    () => ({
      quoteShapes,
      doQuote,
      getCheckoutState,
      setCheckoutState,
      resetVersionMode,
      previewResetQuote,
      quoteVersion,
    }),
    [
      quoteShapes,
      doQuote,
      getCheckoutState,
      setCheckoutState,
      resetVersionMode,
      previewResetQuote,
      quoteVersion,
    ]
  );
};
